/**
 * @module FetchUtils
 */

import { dispatch } from "../src/Dispatcher";
const _polls = [];

/**
 * Serializes a response based on Selkirk Systems response structures, and then
 * dispatches the action provided.
 * 
 * @method
 * 
 * @param {string}      action  - const variable action name
 * @returns {serializeCallbackFn}
 */
export const serializeAndRespondWith = action => {
  /**
  * Callback function for a Fetch call then()
  * @name serializeCallbackFn
  * @method
  * 
  * @param {array}         array             - Response array return by the Fetch library
  * @param {object}        array.response    - data returned from the Fetch call
  * @param {boolean}       array.isAbort     - was the fetch call aborted for some reason.
  * 
  * @returns {array<object,boolean>}
  */
  return ([response, isAbort]) => {
    //Is any status is outside 200 - 299 it is not ok
    if (response.status.code < 200 || response.status.code >= 300 || isAbort) {
      return [response, isAbort];
    }
    let data = {};
    if (Array.isArray(response.data)) {
      dispatch(action, {
        items: response.data
      });
      return [response, isAbort];
    } else {
      data = {
        ...response.data
      };
    }
    if (data._embedded) {
      data.items = data._embedded[[Object.keys(data._embedded)[0]]];
      delete data._embedded;
      dispatch(action, data);
      return [response, isAbort];
    } else {
      data.items = [];
    }
    dispatch(action, data);
    return [response, isAbort];
  };
};
export const serializeData = data => {
  const ret = {
    items: []
  };
  if (Array.isArray(data)) {
    ret.items = data;
    return ret;
  }
  if (data._embedded) {
    ret.items = data._embedded[[Object.keys(data._embedded)[0]]];
    return ret;
  }
  return data;
};

/**
 * Dispatches the provided action with the response from a Fetch call.
 * @method
 * 
 * @param {string}       action  - const variable action name 
 * @returns {serializeCallbackFn}
 */
export const respondWith = action => {
  return ([response, isAbort]) => {
    //Is any status is outside 200 - 299 it is not ok
    if (response.status.code < 200 || response.status.code >= 300 || isAbort) {
      return [response, isAbort];
    }
    dispatch(action, response ? response.data : response);
    return [response, isAbort];
  };
};

/**
 * Pulls out the selkirk systems response structure looking for response._embedded[prop]
 * 
 * @method
 * @deprecated
 * @param {string}          prop -  Property name of the object expected in the Fetch callback 
 * @returns {function}
 */
export const embedded = prop => {
  return response => {
    if (response[0].status?.isAbort) {
      return null;
    }
    return response._embedded[prop];
  };
};
/**
 * Starts polling a function based on a timer and other parameters
 * 
 * @method
 * 
 * @param {function}        func        - Function to be polled
 * @param {number}          timer       - time in milliseconds between polls
 * @param  {...any}         [params]    - parameters to pass to the function being polled.
 * @returns {object}           
 */
export const startPolling = (func, timer, ...params) => {
  const exists = _polls.find(p => p.func === func);
  if (exists) {
    console.warn(`startPolling: Polling function already running (${timer})`);
    return;
  }
  const poll = setInterval(() => func.apply(null, params), timer);
  _polls.push({
    timer: poll,
    func: func,
    interval: timer
  });
  return poll;
};

/**
 * Stops polling a function
 * @method
 * 
 * @param {function} func 
 */
export const stopPolling = func => {
  const index = _polls.findIndex(p => p.func === func);
  if (index > 0) {
    console.warn(`stopPolling: Polling function not found`);
  }
  clearInterval(_polls[index].timer);
  _polls.splice(index, 1);
};